import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Building2, Search, Bot, Network, ArrowUpRight, ChevronRight, Lock, FileText } from "lucide-react";

const ICONS = { company: Building2, seo: Search, scraper: Network, agent: Bot };

export default function Dashboard() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [params] = useSearchParams();
  const planParam = params.get("plan");

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => {
    if (planParam && user) {
      // auto-trigger checkout if user just registered with a plan in URL
      (async () => {
        try {
          const { data } = await api.post("/payments/checkout", { plan_id: planParam, origin_url: window.location.origin });
          window.location.href = data.url;
        } catch (e) {
          toast.error(formatApiError(e));
        }
      })();
    }
  }, [planParam, user]);

  if (!user) return null;
  const plan = user.plan || "free";
  const isSubscribed = user.role === "admin" || ["starter", "pro", "enterprise"].includes(plan);
  const usedPct = stats ? Math.min(100, Math.round((stats.used / Math.max(1, stats.quota)) * 100)) : 0;

  return (
    <div className="min-h-screen flex flex-col bg-[#F2F2F2]" data-testid="dashboard-page">
      <Header />
      <main className="flex-1 max-w-7xl w-full mx-auto px-5 md:px-8 py-12 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14">
          <div className="md:col-span-7">
            <div className="label-eyebrow mb-4">— {t.dashboard.welcome}</div>
            <h1 className="font-serif-display text-4xl md:text-6xl tracking-tighter leading-tight">
              {user.name || user.email}
            </h1>
            <p className="mt-4 text-[#4A4A4A] text-lg max-w-xl">
              {t.about.desc}
            </p>
          </div>

          <div className="md:col-span-5">
            <div className="aq-card-featured p-8 text-[#EAE4D5] rounded-2xl" data-testid="plan-card">
              <div className="relative">
                <div className="flex items-baseline justify-between gap-4">
                  <div>
                    <div className="label-eyebrow !text-[#B6B09F]">{t.dashboard.current}</div>
                    <div className="font-serif-display text-4xl mt-2 capitalize text-[#EAE4D5]">{plan === "free" ? t.dashboard.free : plan}</div>
                  </div>
                  {plan !== "enterprise" && (
                    <button onClick={() => navigate("/#pricing")} className="aq-button-ghost !bg-[#EAE4D5] !text-[#0A0A0A] !border-[#EAE4D5] hover:!bg-white" data-testid="upgrade-btn">
                      {t.dashboard.upgrade}
                    </button>
                  )}
                </div>
                <div className="mt-6 h-px bg-[#EAE4D5]/20" />
                <div className="mt-6 grid grid-cols-2 gap-6">
                  <div>
                    <div className="font-mono-aq text-[0.65rem] text-[#B6B09F] tracking-[0.22em] uppercase">{t.dashboard.used}</div>
                    <div className="text-3xl font-serif-display mt-1 text-[#EAE4D5]">{stats?.used ?? "—"}</div>
                  </div>
                  <div>
                    <div className="font-mono-aq text-[0.65rem] text-[#B6B09F] tracking-[0.22em] uppercase">{t.dashboard.quota}</div>
                    <div className="text-3xl font-serif-display mt-1 text-[#EAE4D5]">{stats?.quota ?? "—"}</div>
                  </div>
                </div>
                <div className="mt-5 h-1 rounded-full bg-[#EAE4D5]/15 overflow-hidden">
                  <div className="h-full bg-[#EAE4D5] rounded-full" style={{ width: `${usedPct}%`, transition: "width 600ms ease" }} />
                </div>
                {user.plan_expires_at && (
                  <div className="mt-5 font-mono-aq text-[0.65rem] tracking-[0.22em] uppercase text-[#B6B09F]">
                    {t.dashboard.expires}: {new Date(user.plan_expires_at).toLocaleDateString(lang === "tr" ? "tr-TR" : "en-US")}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="label-eyebrow mb-6 flex items-center justify-between">
          <span>— {t.dashboard.services_title}</span>
          {isSubscribed && (
            <button
              onClick={() => navigate("/dashboard/reports")}
              className="inline-flex items-center gap-2 font-mono-aq text-[0.7rem] uppercase tracking-[0.2em] text-[#0A0A0A] hover:text-[#1E3A5F]"
              data-testid="dashboard-reports-link"
            >
              <FileText size={13} /> {t.dashboard.reports_open}
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {(stats?.services || []).map((s, i) => {
            const Icon = ICONS[s.key];
            const name = lang === "tr" ? s.name_tr : s.name_en;
            return (
              <article key={s.key} className="aq-card p-7 group cursor-pointer relative" onClick={() => navigate(`/dashboard/services/${s.key}`)} data-testid={`service-tile-${s.key}`}>
                {!isSubscribed && (
                  <div className="absolute top-4 right-4 flex items-center gap-1.5 font-mono-aq text-[0.6rem] uppercase tracking-[0.2em] text-[#4A4A4A]">
                    <Lock size={11} /> Kilitli
                  </div>
                )}
                <div className="flex items-start justify-between">
                  <div className="w-11 h-11 border border-[#B6B09F] flex items-center justify-center group-hover:border-[#1E3A5F] group-hover:text-[#1E3A5F] transition-colors">
                    <Icon size={18} />
                  </div>
                  <span className="font-mono-aq text-[0.65rem] tracking-[0.2em] text-[#B6B09F]">0{i + 1}</span>
                </div>
                <h3 className="font-serif-display text-2xl mt-6">{name}</h3>
                <button className="mt-5 inline-flex items-center gap-2 font-mono-aq text-[0.7rem] uppercase tracking-[0.2em] text-[#0A0A0A] group-hover:text-[#1E3A5F]">
                  {t.dashboard.open} <ChevronRight size={14} />
                </button>
              </article>
            );
          })}
        </div>

        <div className="mt-16 flex justify-end">
          <a href="/#pricing" className="aq-button-ghost" data-testid="dashboard-pricing-link">
            {t.nav.pricing} <ArrowUpRight size={14} />
          </a>
        </div>
      </main>
      <Footer />
    </div>
  );
}
